import { useMemo, useState } from "react";
import {
  CalculationMethods,
  MountTypes,
  RadialSpeed,
  ResidualDisbalance,
  ResidualDisbalanceByPlane,
  SpecificResidualDisbalance,
} from "../../../../utils/helpers/tolerance/GeneralHelper";
import { useTranslation } from "react-i18next";
import CalculationMethodSelector from "../../../../components/Widgets/Tolerance/CalculationMethodSelector/CalculationMethodSelector";
import MountTypeSelector from "../../../../components/Widgets/Tolerance/MountTypeSelector/MountTypeSelector";
import Lengths from "../../../../components/Widgets/Tolerance/Lengths/Lengths";

import multiSupportRotor from "../assets/multi-support-rotor.jpg";
import consoleRotor from "../assets/console-rotor.jpg";
import { Typography } from "@mui/material";
import ResidualDisbalanceDisplayer from "../../../../components/Widgets/Tolerance/ResidualDisbalanceDisplayer/ResidualDisbalanceDisplayer";
import PermissibleUnbalancedMassCalculation from "../../../../components/Widgets/Tolerance/Other/PermissibleUnbalancedMassCalculation/PermissibleUnbalancedMassCalculation.jsx";
import CentrifugalForceCalculation from "../../../../components/Widgets/Tolerance/Other/CentrifugalForceCalculation/CentrifugalForceCalculation.jsx";

export function FanTolerance({ toleranceClass, mass, isDisabled, frequency }) {
  const { t } = useTranslation();
  const [calculationMethod, setCalculationMethod] = useState(
    CalculationMethods.onePlane
  );
  const [mountType, setMountType] = useState(MountTypes.multiSupport);
  const [lengths, setLengths] = useState({
    length: 0,
    lengthLA: 0,
    lengthLB: 0,
  });

  const [radiusA, setRadiusA] = useState(0);
  const [radiusB, setRadiusB] = useState(0);
  const [radius, setRadius] = useState(0);

  const calculationMethods = useMemo(() => {
    return [
      {
        value: CalculationMethods.onePlane,
        label: t(
          "shared.disbalance.components.calculationMethodSelector.methodLabels.onePlane"
        ),
      },
      {
        value: CalculationMethods.dualPlane,
        label: t(
          "shared.disbalance.components.calculationMethodSelector.methodLabels.dualPlane"
        ),
      },
    ];
  }, [t]);

  const isDualPlane = useMemo(
    () => calculationMethod === CalculationMethods.dualPlane,
    [calculationMethod]
  );

  const radialSpeed = useMemo(() => {
    return RadialSpeed(frequency);
  }, [frequency]);

  const residualDisbalance = useMemo(
    () =>
      toleranceClass
        ? ResidualDisbalance(toleranceClass.value, mass, radialSpeed) || 0
        : 0,
    [toleranceClass, mass, radialSpeed]
  );

  const specificResidualDisbalance = useMemo(
    () =>
      residualDisbalance
        ? SpecificResidualDisbalance(residualDisbalance, mass)
        : 0,
    [residualDisbalance, mass]
  );

  const residualDisbalancePerPlaneA = useMemo(() => {
    return (
      ResidualDisbalanceByPlane(
        residualDisbalance,
        lengths.length,
        lengths.lengthLB
      ) || 0
    );
  }, [residualDisbalance, lengths]);
  const residualDisbalancePerPlaneB = useMemo(() => {
    return (
      ResidualDisbalanceByPlane(
        residualDisbalance,
        lengths.length,
        lengths.lengthLA
      ) || 0
    );
  }, [residualDisbalance, lengths]);

  const mountTypes = useMemo(() => {
    return [
      {
        image: { alt: "Multi support", src: multiSupportRotor },
        value: MountTypes.multiSupport,
        label: t("shared.disbalance.components.mountTypeSelector.types.multiSupport"),
        help: (
          <>
            <Typography>{t("shared.disbalance.components.mountTypeSelector.supportPlanes")}</Typography>
            <Typography>
              {t("pages.fanTolerance.components.mountTypeSelector.multiSupportCenterMass")}
            </Typography>
          </>
        ),
      },
      {
        image: { alt: "Console", src: consoleRotor },
        value: MountTypes.console,
        label: t("shared.disbalance.components.mountTypeSelector.types.console"),
        help: (
          <>
            <Typography>{t("shared.disbalance.components.mountTypeSelector.supportPlanes")}</Typography>
            <Typography>
              {t("pages.fanTolerance.components.mountTypeSelector.consoleCenterMass")}
            </Typography>
          </>
        ),
      },
    ];
  }, [t]);

  return (
    !isDisabled && (
      <>
        <CalculationMethodSelector
          disabled={isDisabled}
          onChange={setCalculationMethod}
          titleText={t("pages.fanTolerance.components.calculationMethodSelector.title")}
          calculationMethods={calculationMethods}
          defaultCalculationMethod={CalculationMethods.onePlane}
          value={calculationMethod}
        />
        {isDualPlane && (
          <>
            {/* Mount Type */}
            <MountTypeSelector
              mountTypes={mountTypes}
              onChange={setMountType}
              value={mountType}
              defaultMountType={MountTypes.multiSupport}
            />
            {/* Inputs for Dimensions */}
            <Lengths lengths={lengths} onChange={setLengths} />
            <br />
            <br />
          </>
        )}
        <ResidualDisbalanceDisplayer
          title={t("shared.disbalance.result")}
          isDualPlane={isDualPlane}
          residualDisbalance={residualDisbalance}
          specificResidualDisbalance={specificResidualDisbalance}
          residualDisbalancePerPlaneA={residualDisbalancePerPlaneA}
          residualDisbalancePerPlaneB={residualDisbalancePerPlaneB}
          mountType={mountType}
          totalDisbalanceHelpButtonText={t("pages.fanTolerance.components.residualDisbalanceDisplayer.components.totalDisbalance.helpButtonText")}
        />
        <Typography variant="h4" sx={{ my: 3 }}>
          {t("shared.disbalance.additionalCalculations")}
        </Typography>
        <PermissibleUnbalancedMassCalculation
          isDualPlane={isDualPlane}
          residualDisbalancePerPlaneA={residualDisbalancePerPlaneA}
          residualDisbalancePerPlaneB={residualDisbalancePerPlaneB}
          residualDisbalance={residualDisbalance}
          setRadiusA={setRadiusA}
          setRadiusB={setRadiusB}
          setRadius={setRadius}
          radialSpeed={radialSpeed}
          radiusA={radiusA}
          radiusB={radiusB}
          radius={radius}
        />
        <CentrifugalForceCalculation
          isDualPlane={isDualPlane}
          mass={mass}
          radiusA={radiusA}
          radiusB={radiusB}
          radius={radius}
          radialSpeed={radialSpeed}
          setRadiusA={setRadiusA}
          setRadiusB={setRadiusB}
          setRadius={setRadius}
        />
      </>
    )
  );
}
